import { SITUACAO_LABELS, ORIGEM_LABELS } from "@bookdental/schemas";
import { reais, type LancamentoDaLista } from "@/lib/dinheiro";
import { cn } from "@/lib/cn";
import { AcoesDoLancamento } from "./AcoesDoLancamento";

const COR_DA_SITUACAO = {
  PAGO: "bg-emerald-50 text-emerald-700 dark:bg-emerald-500/10 dark:text-emerald-400",
  VENCIDO: "bg-red-50 text-red-700 dark:bg-red-500/10 dark:text-red-400",
  PREVISTO: "bg-amber-50 text-amber-700 dark:bg-amber-500/10 dark:text-amber-400",
  ESTORNADO: "bg-zinc-100 text-zinc-500 dark:bg-muted dark:text-muted-foreground",
} as const;

const data = (t: string | null) => (t ? t.split("-").reverse().join("/") : "—");

function Situacao({ l }: { l: LancamentoDaLista }) {
  const rotulo = l.ehEstorno ? "Estorno" : l.situacao === "PAGO" && l.tipo === "ENTRADA" ? "Recebido" : SITUACAO_LABELS[l.situacao];
  return (
    <span className={cn("rounded-full px-2 py-0.5 text-[10.5px] font-bold uppercase", COR_DA_SITUACAO[l.ehEstorno ? "ESTORNADO" : l.situacao])}>
      {rotulo}
    </span>
  );
}

function Valor({ l }: { l: LancamentoDaLista }) {
  return (
    <span
      className={cn(
        "font-semibold tabular-nums",
        !l.contaNoTotal ? "text-zinc-400 line-through" : l.tipo === "ENTRADA" ? "text-emerald-700 dark:text-emerald-400" : "text-zinc-900 dark:text-foreground",
      )}
    >
      {l.tipo === "SAIDA" ? "− " : "+ "}
      {reais(l.valorCents)}
    </span>
  );
}

const Marcador = ({ cor }: { cor: string | null }) => <span className="h-3 w-3 shrink-0 rounded-[3px]" style={{ backgroundColor: cor || "#A1A1AA" }} />;

export function LinhasDeLancamento({ linhas, podeLancar, onEditar }: { linhas: LancamentoDaLista[]; podeLancar: boolean; onEditar: (l: LancamentoDaLista) => void }) {
  return (
    <div className="overflow-hidden rounded-xl border border-zinc-200 bg-white shadow-sm dark:border-border dark:bg-card">
      <ul className="divide-y divide-zinc-100 md:hidden dark:divide-border/50">
        {linhas.map((l) => (
          <li key={l.id} className="flex items-start gap-3 px-4 py-3">
            <Marcador cor={l.categoria.cor} />
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-semibold text-zinc-900 dark:text-foreground">{l.descricao}</p>
              <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">
                {l.categoria.nome}
                {l.vencimento && ` · vence ${data(l.vencimento)}`}
              </p>
              <div className="mt-1">
                <Situacao l={l} />
              </div>
            </div>
            <div className="flex flex-col items-end gap-1">
              <Valor l={l} />
              {podeLancar && <AcoesDoLancamento l={l} onEditar={() => onEditar(l)} />}
            </div>
          </li>
        ))}
      </ul>

      <table className="hidden w-full text-sm md:table">
        <thead className="border-b border-zinc-100 bg-zinc-50/60 text-[10.5px] uppercase tracking-wider text-zinc-500 dark:border-border dark:bg-muted/30 dark:text-muted-foreground">
          <tr>
            <th className="px-4 py-2.5 text-left font-semibold">Descrição</th>
            <th className="px-2 py-2.5 text-left font-semibold">Categoria</th>
            <th className="hidden px-2 py-2.5 text-left font-semibold lg:table-cell">Cliente ou fornecedor</th>
            <th className="px-2 py-2.5 text-left font-semibold">Vencimento</th>
            <th className="px-2 py-2.5 text-left font-semibold">Situação</th>
            <th className="px-4 py-2.5 text-right font-semibold">Valor</th>
            {podeLancar && <th className="w-12" />}
          </tr>
        </thead>
        <tbody>
          {linhas.map((l) => (
            <tr key={l.id} className="border-t border-zinc-100 first:border-t-0 hover:bg-zinc-50/60 dark:border-border/50 dark:hover:bg-muted/40">
              <td className="px-4 py-2.5">
                <p className="font-semibold text-zinc-900 dark:text-foreground">
                  {l.descricao}
                  {l._count.notas > 0 && <span className="ml-2 rounded bg-zinc-100 px-1.5 py-0.5 text-[10px] font-bold text-zinc-600 dark:bg-muted dark:text-muted-foreground">NF</span>}
                </p>
                {l.origem !== "MANUAL" && <p className="text-[11px] text-zinc-500 dark:text-muted-foreground">{ORIGEM_LABELS[l.origem]}</p>}
              </td>
              <td className="px-2 py-2.5">
                <span className="inline-flex items-center gap-2 text-zinc-700 dark:text-foreground/80">
                  <Marcador cor={l.categoria.cor} />
                  {l.categoria.nome}
                </span>
              </td>
              <td className="hidden px-2 py-2.5 text-zinc-600 lg:table-cell dark:text-foreground/70">{l.partner?.name ?? "—"}</td>
              <td className="px-2 py-2.5 tabular-nums text-zinc-600 dark:text-foreground/70">{data(l.vencimento)}</td>
              <td className="px-2 py-2.5">
                <Situacao l={l} />
              </td>
              <td className="px-4 py-2.5 text-right">
                <Valor l={l} />
              </td>
              {podeLancar && (
                <td className="pr-3 text-right">
                  <AcoesDoLancamento l={l} onEditar={() => onEditar(l)} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
